import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, type MenuProps } from 'antd';
import React from 'react';
import styled from 'styled-components';

import type Bundle from 'stores/Bundle';
import type BundleElement from 'stores/BundleElement';
import { encodeURIPathname } from 'utils/String';

const Container = styled.div`
  border-bottom: 1px solid #e5e5e5;
  display: grid;
  gap: 5px;
  grid-template-columns: 1fr auto auto;
  padding: 10px 0;
  width: 100%;
`;

const MetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FileLabelContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const PrincipalAnchor = styled.a`
  font-size: 1em;
  font-weight: bold;
`;

const FileSizeReport = styled.div`
  display: flex;
`;

const ExclamationCircleIcon = styled(ExclamationCircleOutlined)`
  margin-left: 0.25em;
`;

interface FileSizeStatProps {
  $alert?: boolean;
}

const FileSizeStat = styled.div<FileSizeStatProps>`
  width: 15em;
  ${(props) => props.$alert && 'color: #d00;'}

  &:last-child {
    width: auto;
  }
`;

const EllipsisIcon = styled(EllipsisOutlined)`
  margin-top: 4px;
`;

const FileImageIcon = styled(FileImageOutlined)`
  margin-top: 4px;
`;

const DropdownButton = styled(Button)`
  padding: 10px;
  display: flex;
  align-items: center;
`;

// Add this new styled component for the error message
const ErrorContainer = styled.div`
  color: red;
  padding: 10px;
  border: 1px solid red;
  margin: 10px 0;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 7px;
  align-items: center;
`;

function resolveBundleURL(base: string, storagePath: string) {
  const u = new URL(encodeURIPathname(storagePath), base);
  return u.href;
}

export interface BundleElementRowProps {
  bundle: Bundle;
  element: BundleElement;
}

function BundleElementRow({ bundle, element }: BundleElementRowProps) {
  const distBuild = element.data.builds.find(
    (build) => build.buildType === 'dist'
  );

  const sourceBuild = element.data.builds.find(
    (build) => build.buildType === 'source'
  );

  if (!distBuild || !sourceBuild) {
    return (
      <ErrorContainer>
        Error: No distribution build found for {element.data.label}
      </ErrorContainer>
    );
  }

  // ??? Divisors are different. Maybe it's meant to be a least-favorable
  // interpretation?
  // actually no - least favorable would be to use the greater divisor
  // for the max. so i don't know what the intent was here
  const currentZipSize = distBuild.archiveActualSize / 1024;
  const maxZipSize = distBuild.archiveMaxSize / 1000;

  const isZipOverSize = currentZipSize > maxZipSize;
  const zipSizeLabelApprox = `${currentZipSize.toFixed(1)} / ${maxZipSize.toFixed(1)}K`;
  const zipSizeLabelExact = `${distBuild.archiveActualSize} / ${distBuild.archiveMaxSize} bytes`;

  // Divisors are also different here
  const lossyImageActualSize = element.data.lossyScreenshotActualSize / 1024;
  const lossyImageMaxSize = element.data.lossyScreenshotMaxSize / 1000;

  const isImageOverSize = lossyImageActualSize > lossyImageMaxSize;
  const imageSizeLabelApprox = `${lossyImageActualSize.toFixed(1)} / ${lossyImageMaxSize.toFixed(1)}K`;
  const imageSizeLabelExact = `${element.data.lossyScreenshotActualSize} / ${element.data.lossyScreenshotMaxSize} bytes`;

  const lossyScreenshotURL = resolveBundleURL(
    bundle.baseURL,
    element.data.lossyScreenshotPath
  );

  const losslessScreenshotURL = resolveBundleURL(
    bundle.baseURL,
    element.data.losslessScreenshotPath
  );

  const archiveDistURL = resolveBundleURL(
    bundle.baseURL,
    distBuild.archiveStorage
  );

  const archiveSourceURL = resolveBundleURL(
    bundle.baseURL,
    sourceBuild.archiveStorage
  );

  const menuSeedData = [
    { label: 'Download ZIP', href: archiveDistURL },
    { label: 'Download Source ZIP', href: archiveSourceURL },
    { label: 'View Bundled Ad', href: element.stagingURL },
    { label: 'View Static Backup', href: lossyScreenshotURL },
    { label: 'View Screenshot PNG', href: losslessScreenshotURL },
  ];

  const menu: MenuProps = {
    items: menuSeedData.map((s) => ({
      label: (
        <a href={s.href} target="_blank" rel="noreferrer">
          {s.label}
        </a>
      ),
      key: s.label,
    })),
  };

  return (
    <Container>
      <MetadataContainer>
        <FileLabelContainer>
          <PrincipalAnchor
            href={`${element.stagingURL}`}
            target="_blank"
            rel="noreferrer"
          >
            {element.data.label}
          </PrincipalAnchor>

          <PrincipalAnchor
            title="Backup Image"
            href={lossyScreenshotURL}
            target="_blank"
            rel="noreferrer"
          >
            <FileImageIcon title="Backup Image" />
          </PrincipalAnchor>
        </FileLabelContainer>

        <FileSizeReport>
          <FileSizeStat $alert={isZipOverSize}>
            <b>Zip size </b>
            <span title={zipSizeLabelExact}>{zipSizeLabelApprox}</span>
            {isZipOverSize && <ExclamationCircleIcon />}
          </FileSizeStat>

          <FileSizeStat $alert={isImageOverSize}>
            <b>Backup Image size </b>
            <span title={imageSizeLabelExact}>{imageSizeLabelApprox}</span>
            {isImageOverSize && <ExclamationCircleIcon />}
          </FileSizeStat>
        </FileSizeReport>
      </MetadataContainer>

      <ButtonsContainer>
        <Dropdown trigger={['click']} menu={menu}>
          <DropdownButton>
            <EllipsisIcon />
          </DropdownButton>
        </Dropdown>
      </ButtonsContainer>
    </Container>
  );
}

export default BundleElementRow;
